import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaTrash, FaSave, FaTimes, FaEraser, FaEye, FaEdit, FaSearch } from "react-icons/fa";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import classes from "./PrescriptionManagement.module.css";
import ConfirmModal from "../../../Components/UI/ConfirmModal";
import DataModal from "../../UI/DataModal";
import AuthContext from "../../../store/auth-context";

const PrescriptionManagement = () => {
    const { t } = useTranslation();
    const authCtx = useContext(AuthContext);
    const [prescriptionsGrouped, setPrescriptionsGrouped] = useState({});
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Row level editing in temporary table
    const [editingIndex, setEditingIndex] = useState(null);
    const [editRowData, setEditRowData] = useState(null);

    // Form States
    const [diagnosis, setDiagnosis] = useState("");
    const [prescriptionSearchTerm, setPrescriptionSearchTerm] = useState("");
    const [prescribedMedicines, setPrescribedMedicines] = useState([]);
    const [diagnosisSuggestions, setDiagnosisSuggestions] = useState([]);
    const [modalData, setModalData] = useState({});

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: "",
        message: "",
        onConfirm: () => { },
        variant: "danger"
    });

    // Medicine Input States
    const initialMedState = {
        medicineName: "",
        type: "",
        dosage: "",
        breakFastDose: null,
        lunchDose: null,
        dinnerDose: null,
        instruction: "",
        days: "",
    };
    const [medForm, setMedForm] = useState(initialMedState);
    const [medicinesList, setMedicinesList] = useState([]); // for search suggestions

    const fetchDiagnosisSuggestions = async (term) => {
        if (!term || term.trim() === "") {
            setDiagnosisSuggestions([]);
            return;
        }
        const token = getAuthToken();
        const apiChar = getApiChara();
        try {
            const response = await fetch(`${apiChar}/diagnosis/${encodeURIComponent(term)}`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                // Map to diagnosisName as per check-patient page implementation
                setDiagnosisSuggestions(Array.isArray(data) ? data.map(d => d.diagnosisName) : []);
            }
        } catch (error) {
            console.error("Error fetching diagnosis suggestions:", error);
        }
    };

    const fetchSavedPrescriptions = async (diag = "") => {
        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const url = diag
                ? `${apiChar}/savedPrescription/getSavedPrescriptionListByDiagnosis?diagnosis=${encodeURIComponent(diag)}`
                : `${apiChar}/savedPrescription/getSavedPrescriptionListByDiagnosis?diagnosis=`;

            const response = await fetch(url, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setPrescriptionsGrouped(data || {});
            } else {
                setPrescriptionsGrouped({});
            }
        } catch (error) {
            console.error("Error fetching prescriptions:", error);
            setPrescriptionsGrouped({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSavedPrescriptions();
    }, []);

    const onSearchTemplate = async (term) => {
        if (!term || !term.trim()) {
            toast.warning(t("Please enter diagnosis name to search"));
            return;
        }
        const token = getAuthToken();
        const apiChar = getApiChara();
        const apiUrl = `${apiChar}/savedPrescription/getSavedPrescriptionListByDiagnosis?diagnosis=${encodeURIComponent(term.trim())}`;
        try {
            const response = await fetch(apiUrl, {
                headers: { Authorization: "Bearer " + token },
            });
            if (!response.ok) throw new Error("Fetch failed");
            const data = await response.json();
            if (Object.keys(data).length === 0) {
                toast.info(t("No templates found for this diagnosis"));
            } else {
                setModalData(data);
            }
        } catch (error) {
            console.error("Error searching template:", error);
            toast.error(t("Error searching template"));
        }
    };

    const handleDataClose = () => setModalData({});

    const handleAddToForm = (medicines) => {
        if (!medicines || medicines.length === 0) return;

        // Load diagnosis from first item
        setDiagnosis(medicines[0].diagnosis || medicines[0].notes || "");

        const normalized = medicines.map(m => ({
            id: Date.now() + Math.random(),
            medicineName: m.medicineName,
            type: m.type,
            dosage: m.dosage,
            breakFastDose: m.breakFastDose,
            lunchDose: m.lunchDose,
            dinnerDose: m.dinnerDose,
            instruction: m.instruction,
            days: m.days
        }));
        setPrescribedMedicines(normalized);
        handleDataClose();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.info(t("Prescription template loaded."));
    }

    const onSearchMedicine = async (term) => {
        if (!term) return;
        const token = getAuthToken();
        const apiChar = getApiChara();
        try {
            const response = await fetch(`${apiChar}/medicine/getMedicinesByNameOrBarcodeOrDisease?name=${encodeURIComponent(term)}`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setMedicinesList(data || []);
            }
        } catch (err) {
            console.error("Error searching medicines:", err);
        }
    };

    const handleMedInputChange = (e) => {
        const { name, value } = e.target;
        setMedForm({ ...medForm, [name]: value });
        if (name === "medicineName") {
            onSearchMedicine(value);

            // Auto-select medicine type
            const found = medicinesList.find(m => m.name.toUpperCase() === value.toUpperCase());
            if (found && found.medType) {
                setMedForm(prev => ({ ...prev, medicineName: value, type: found.medType.toUpperCase() }));
            }
        }
    };

    const setDose = (key, value) => {
        setMedForm(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
    };

    const addMedicineToTable = () => {
        if (!medForm.medicineName) {
            toast.error(t("Medicine Name is required"));
            return;
        }
        setPrescribedMedicines([...prescribedMedicines, { ...medForm, id: Date.now() }]);
        setMedForm(initialMedState);
    };

    const removeMedicineFromTable = (id) => {
        setPrescribedMedicines(prescribedMedicines.filter(m => m.id !== id));
    };

    const handleRowEdit = (m, index) => {
        setEditingIndex(index);
        setEditRowData({ ...m });
    };

    const handleRowSave = (index) => {
        const updated = prescribedMedicines.map((m, i) => i === index ? editRowData : m);
        setPrescribedMedicines(updated);
        setEditingIndex(null);
        setEditRowData(null);
    };

    const handleRowCancel = () => {
        setEditingIndex(null);
        setEditRowData(null);
    };

    const handleRowDataChange = (e, field) => {
        setEditRowData({ ...editRowData, [field]: e.target.value });
    };

    const handleClearAllMedicines = () => {
        if (prescribedMedicines.length === 0) return;
        setConfirmModal({
            show: true,
            title: t("Clear All Medicines"),
            message: t("Are you sure you want to clear all added medicines?"),
            variant: "danger",
            onConfirm: () => {
                setPrescribedMedicines([]);
                setConfirmModal(prev => ({ ...prev, show: false }));
            }
        });
    };

    const resetPage = () => {
        setDiagnosis("");
        setPrescribedMedicines([]);
        setMedForm(initialMedState);
        setEditingId(null);
    };

    const handleSavePrescription = async () => {
        if (!diagnosis) {
            toast.error(t("Diagnosis is required"));
            return;
        }
        if (prescribedMedicines.length === 0) {
            toast.error(t("Add at least one medicine"));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();

        const now = new Date();
        const currentDateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

        const payload = prescribedMedicines.map(med => ({
            id: editingId ? med.id : 0,
            patientId: {},
            doctorId: {},
            checkUp: {},
            checkUpId: 0,
            dateTime: currentDateTime,
            medicineName: med.medicineName,
            breakFastDose: med.breakFastDose || "",
            lunchDose: med.lunchDose || "",
            dinnerDose: med.dinnerDose || "",
            notes: diagnosis,
            instruction: med.instruction || "",
            type: med.type || "",
            days: parseInt(med.days) || 0,
            dosage: med.dosage || ""
        }));

        const url = `${apiChar}/savedPrescription/savePrescription?diagnosis=${encodeURIComponent(diagnosis)}`;

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(t("Prescription saved successfully!"));
                resetPage();
                fetchSavedPrescriptions();
            } else {
                throw new Error("Failed to save prescription");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error saving prescription"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCopyGroup = (medicines) => {
        if (!medicines || medicines.length === 0) return;

        setDiagnosis(medicines[0].diagnosis || medicines[0].notes || "");

        const normalized = medicines.map(m => ({
            id: Date.now() + Math.random(),
            medicineName: m.medicineName,
            type: m.type,
            dosage: m.dosage,
            breakFastDose: m.breakFastDose,
            lunchDose: m.lunchDose,
            dinnerDose: m.dinnerDose,
            instruction: m.instruction,
            days: m.days
        }));

        setPrescribedMedicines(normalized);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.info(t("Prescription template loaded."));
    };

    const handleDeleteSavedPrescription = (preNumber) => {
        const clientId = authCtx.tenantOrClientId;

        setConfirmModal({
            show: true,
            title: t("Delete Prescription Template"),
            message: t("Are you sure you want to delete this Entire prescription template?"),
            variant: "danger",
            onConfirm: async () => {
                const token = getAuthToken();
                const apiChar = getApiChara();
                try {
                    const url = `${apiChar}/savedPrescription/deleteSavedPrescriptionRecordsByPreNumber?preNumber=${preNumber}&tenantOrClientId=${clientId}`;
                    const response = await fetch(url, {
                        method: "DELETE",
                        headers: { Authorization: "Bearer " + token },
                    });
                    if (response.ok) {
                        toast.success(t("Saved prescription template deleted successfully!"));
                        fetchSavedPrescriptions(prescriptionSearchTerm);
                    } else {
                        toast.error(t("Failed to delete template"));
                    }
                } catch (error) {
                    console.error(error);
                    toast.error(t("Error deleting template"));
                } finally {
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const renderToggle = (key, label) => (
        <div className={classes.doseBox}>
            <div className={classes.doseLabel}>{t(label)}</div>
            <div className={classes.doseButtons}>
                <button
                    type="button"
                    className={`${classes.doseBtn} ${medForm[key] === 'BEFORE' ? classes.doseBtnActive : ''}`}
                    onClick={() => setDose(key, 'BEFORE')}
                >
                    {t("Before")}
                </button>
                <button
                    type="button"
                    className={`${classes.doseBtn} ${medForm[key] === 'AFTER' ? classes.doseBtnActive : ''}`}
                    onClick={() => setDose(key, 'AFTER')}
                >
                    {t("After")}
                </button>
            </div>
        </div>
    );

    const medTypes = ["TAB", "CAP", "SYR", "SUSP", "SAC", "INJ", "DROP", "EDRP", "LIQ", "OINT", "CRE", "GEL", "LOT", "SPRAY", "SUPP", "ROTACAP", "POWDER"];

    return (
        <div className="prescription-management">
            {Object.keys(modalData).length > 0 && (
                <DataModal
                    prescriptions={modalData}
                    OnCloseModalBlock={handleDataClose}
                    onAddToMedicineTable={handleAddToForm}
                />
            )}

            <div className={classes.cardview}>
                <div className={classes.sectionHeader}>
                    <h5>
                        {editingId ? t("Update Prescription") : t("Create Prescription Template")}
                    </h5>
                </div>

                <div className={classes.form}>
                    <div className="row g-3">
                        <div className="col-md-6 col-12">
                            <label className={classes.label}>{t("Diagnosis")}</label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    style={{ border: "1.5px solid #dcdde1" }}
                                    value={diagnosis}
                                    onChange={(e) => {
                                        setDiagnosis(e.target.value);
                                        fetchDiagnosisSuggestions(e.target.value);
                                    }}
                                    placeholder={t("Enter Diagnosis...")}
                                    list="diag-suggestions-top"
                                />
                                <datalist id="diag-suggestions-top">
                                    {diagnosisSuggestions.map((d, idx) => <option key={idx} value={d} />)}
                                </datalist>
                                <button
                                    className={classes.btnAdd}
                                    style={{ height: '39px', borderRadius: '0 10px 10px 0' }}
                                    type="button"
                                    onClick={() => onSearchTemplate(diagnosis)}
                                >
                                    <FaSearch size={14} />
                                </button>
                            </div>
                        </div>

                        <hr className="my-2" />

                        <div className="col-md-2 col-6">
                            <label className={classes.label}>{t("Type")}</label>
                            <select name="type" value={medForm.type} onChange={handleMedInputChange}>
                                <option value="">{t("Select")}</option>
                                {medTypes.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                        </div>

                        <div className="col-md-4 col-6">
                            <label className={classes.label}>{t("Medicine")}</label>
                            <input
                                type="text"
                                name="medicineName"
                                value={medForm.medicineName}
                                onChange={handleMedInputChange}
                                placeholder={t("Search Medicine...")}
                                list="medicines-datalist-template"
                            />
                            <datalist id="medicines-datalist-template">
                                {medicinesList.map((m, idx) => <option key={idx} value={m.name} />)}
                            </datalist>
                        </div>

                        <div className="col-md-1 col-6">
                            <label className={classes.label}>{t("Dosage")}</label>
                            <input
                                type="text"
                                name="dosage"
                                value={medForm.dosage}
                                onChange={handleMedInputChange}
                            />
                        </div>

                        <div className="col-md-5 col-12 d-flex gap-2">
                            <div className="flex-grow-1">{renderToggle('breakFastDose', 'Breakfast')}</div>
                            <div className="flex-grow-1">{renderToggle('lunchDose', 'Lunch')}</div>
                            <div className="flex-grow-1">{renderToggle('dinnerDose', 'Dinner')}</div>
                        </div>

                        <div className="col-md-5 col-12 mt-3">
                            <label className={classes.label}>{t("Instructions")}</label>
                            <input
                                type="text"
                                name="instruction"
                                value={medForm.instruction}
                                onChange={handleMedInputChange}
                                placeholder={t("Special instructions...")}
                            />
                        </div>

                        <div className="col-md-2 col-6 mt-3">
                            <label className={classes.label}>{t("Days")}</label>
                            <input
                                type="number"
                                name="days"
                                value={medForm.days}
                                onChange={handleMedInputChange}
                            />
                        </div>

                        <div className="col-md-5 col-12 mt-3 d-flex align-items-end gap-2">
                            <button type="button" className={classes.btnClear} onClick={() => setMedForm(initialMedState)}>
                                <FaEraser className="me-2" /> {t("Clear")}
                            </button>
                            <button type="button" className={classes.btnAdd} onClick={addMedicineToTable}>
                                <FaPlus className="me-2" /> {t("Add Medicine")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Temporary Medicine Table */}
                <div className="mt-4">
                    <h6 style={{ fontWeight: "700", fontSize: "0.9rem" }}>{t("Prescribed Medicines")}</h6>
                    <div className="table-responsive border rounded">
                        <Table striped hover className={`${classes.prescTable} mb-0`}>
                            <thead>
                                <tr>
                                    <th>{t("Sr.No.")}</th>
                                    <th>{t("Name")}</th>
                                    <th>{t("Type")}</th>
                                    <th>{t("Dosage")}</th>
                                    <th>{t("Breakfast")}</th>
                                    <th>{t("Lunch")}</th>
                                    <th>{t("Dinner")}</th>
                                    <th>{t("Days")}</th>
                                    <th>{t("Instr")}</th>
                                    <th className="text-center" style={{ width: "120px" }}>
                                        <button type="button" className={classes.btnClearAll} onClick={handleClearAllMedicines}>
                                            <FaTrash className="me-2" /> {t("Clear All")}
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {prescribedMedicines.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-3 text-muted">{t("No medicines prescribed")}</td>
                                    </tr>
                                ) : (
                                    prescribedMedicines.map((m, idx) => (
                                        <tr key={m.id || idx}>
                                            <td>{idx + 1}</td>
                                            {editingIndex === idx ? (
                                                <>
                                                    <td>
                                                        <input
                                                            value={editRowData.medicineName}
                                                            onChange={(e) => handleRowDataChange(e, "medicineName")}
                                                            style={{ width: "100%", padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editRowData.type}
                                                            onChange={(e) => handleRowDataChange(e, "type")}
                                                            style={{ width: "100%", padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        >
                                                            <option value="">{t("Select")}</option>
                                                            {medTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            value={editRowData.dosage}
                                                            onChange={(e) => handleRowDataChange(e, "dosage")}
                                                            style={{ width: "40px", padding: "2px 5px", textAlign: "center", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editRowData.breakFastDose || ""}
                                                            onChange={(e) => handleRowDataChange(e, "breakFastDose")}
                                                            style={{ padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        >
                                                            <option value="">-</option>
                                                            <option value="BEFORE">BEFORE</option>
                                                            <option value="AFTER">AFTER</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editRowData.lunchDose || ""}
                                                            onChange={(e) => handleRowDataChange(e, "lunchDose")}
                                                            style={{ padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        >
                                                            <option value="">-</option>
                                                            <option value="BEFORE">BEFORE</option>
                                                            <option value="AFTER">AFTER</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={editRowData.dinnerDose || ""}
                                                            onChange={(e) => handleRowDataChange(e, "dinnerDose")}
                                                            style={{ padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        >
                                                            <option value="">-</option>
                                                            <option value="BEFORE">BEFORE</option>
                                                            <option value="AFTER">AFTER</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={editRowData.days}
                                                            onChange={(e) => handleRowDataChange(e, "days")}
                                                            style={{ width: "40px", padding: "2px 5px", textAlign: "center", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            value={editRowData.instruction}
                                                            onChange={(e) => handleRowDataChange(e, "instruction")}
                                                            style={{ width: "100%", padding: "2px 5px", borderRadius: "4px", border: "1px solid #ccc" }}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Button variant="link" className="text-success p-1 me-1" onClick={() => handleRowSave(idx)}>
                                                            <FaSave size={14} />
                                                        </Button>
                                                        <Button variant="link" className="text-danger p-1" onClick={handleRowCancel}>
                                                            <FaTimes size={14} />
                                                        </Button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="text-start ps-3">{m.medicineName}</td>
                                                    <td>{m.type}</td>
                                                    <td>{m.dosage}</td>
                                                    <td>{t(m.breakFastDose || "-")}</td>
                                                    <td>{t(m.lunchDose || "-")}</td>
                                                    <td>{t(m.dinnerDose || "-")}</td>
                                                    <td>{m.days}</td>
                                                    <td>{m.instruction}</td>
                                                    <td className="text-center">
                                                        <Button variant="link" className="text-primary p-0 me-2" onClick={() => handleRowEdit(m, idx)}>
                                                            <FaEdit size={14} />
                                                        </Button>
                                                        <Button variant="link" className="text-danger p-0" onClick={() => removeMedicineFromTable(m.id)}>
                                                            <FaTrash size={14} />
                                                        </Button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                    {prescribedMedicines.length > 0 && (
                        <div className="mt-3 d-flex gap-2">
                            <button onClick={handleSavePrescription} className={classes.btnAdd} style={{ width: "auto" }} disabled={isSaving}>
                                {isSaving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
                                {editingId ? t("Update Prescription") : t("Save Prescription")}
                            </button>
                            <button className={classes.btnClear} style={{ width: "auto" }} onClick={resetPage}>
                                <FaTimes className="me-2" /> {t("Cancel")}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="prescriptions-list mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className={classes.sectionHeader} style={{ marginBottom: 0 }}>
                        <h5>{t("Saved Prescriptions")}</h5>
                    </div>
                    <div className="d-flex gap-2">
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={t("Filter by Diagnosis...")}
                                value={prescriptionSearchTerm}
                                onChange={(e) => {
                                    setPrescriptionSearchTerm(e.target.value);
                                    fetchDiagnosisSuggestions(e.target.value);
                                }}
                                list="diag-suggestions-bottom"
                                style={{ width: "220px", border: "1.2px solid #ccc", fontSize: '0.85rem' }}
                            />
                            <datalist id="diag-suggestions-bottom">
                                {diagnosisSuggestions.map((d, idx) => <option key={idx} value={d} />)}
                            </datalist>
                            <button className={classes.btnAdd} style={{ height: "35px", padding: "0 15px", borderRadius: '0 6px 6px 0' }} onClick={() => fetchSavedPrescriptions(prescriptionSearchTerm)}>
                                <FaSearch size={12} className="me-1" /> {t("Search")}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="border-0 shadow-sm rounded-3 overflow-hidden">
                    <Table hover className={`${classes.prescTable} mb-0`}>
                        <thead>
                            <tr>
                                <th style={{ width: "60px", textAlign: "center" }}>{t("Sr.")}</th>
                                <th style={{ width: "200px" }}>{t("Diagnosis")}</th>
                                <th>{t("Prescription Details")}</th>
                                <th style={{ width: "220px", textAlign: "center" }}>{t("Actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4" className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>
                            ) : Object.keys(prescriptionsGrouped).length === 0 ? (
                                <tr><td colSpan="4" className="text-center py-3 text-muted">{t("No prescriptions found")}</td></tr>
                            ) : (
                                Object.entries(prescriptionsGrouped).map(([preNumber, medicines], index) => (
                                    <tr key={preNumber}>
                                        <td className="text-center fw-bold text-muted">{index + 1}</td>
                                        <td className={classes.diagnosisCell}>
                                            {medicines[0]?.diagnosis || medicines[0]?.notes || t("N/A")}
                                        </td>
                                        <td>
                                            <div className={classes.medicineGrid}>
                                                {medicines.map((m, i) => (
                                                    <div key={m.id || i} className={classes.medChip}>
                                                        <span className={classes.medName}>{m.medicineName}</span>
                                                        <div className={classes.medDetail}>
                                                            <span>{m.type} • {m.days}{t("D")}</span>
                                                            <div className="d-flex gap-1">
                                                                {m.breakFastDose && <span className={classes.doseTag}>B</span>}
                                                                {m.lunchDose && <span className={classes.doseTag}>L</span>}
                                                                {m.dinnerDose && <span className={classes.doseTag}>D</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <Button variant="link" className={`${classes.actionBtn} ${classes.selectBtn} me-2`} onClick={() => handleCopyGroup(medicines)}>
                                                <FaPlus size={12} /> {t("Select")}
                                            </Button>
                                            <Button variant="link" className={`${classes.actionBtn} ${classes.deleteBtn}`} onClick={() => handleDeleteSavedPrescription(preNumber)}>
                                                <FaTrash size={12} /> {t("Delete")}
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            <ConfirmModal
                show={confirmModal.show}
                onHide={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                variant={confirmModal.variant}
            />
        </div>
    );
};

export default PrescriptionManagement;
